// Header.jsx
import { useState, useEffect } from 'react';
import { readToken, readName } from './AccountPanel';
import { effectiveTheme, applyTheme } from '../utils/theme';
import Switch from './Switch';

/** "Priya Sharma" -> "Priya". Firms often register as "Studio 17 Architects",
 *  so a first token can be a word rather than a name — that is still the right
 *  thing to show, and the full name is one hover away in the title. */
const firstName = (full) => {
  const t = String(full || '').trim().split(/\s+/)[0] || '';
  return t.length > 14 ? `${t.slice(0, 13)}…` : t;
};

function Header({ rightMode, onChangeRightMode }) {
  // Seeded from the stored token so a reload does not flash "Login" at someone
  // who is already signed in. It is only a guess until the session is checked:
  // AccountPanel announces the real answer on decon:auth once /me replies, and
  // that corrects this either way.
  const [signedIn, setSignedIn] = useState(() => !!readToken());
  const [name, setName] = useState(readName);
  // A slider is binary, so the stored preference becomes binary the moment it is
  // touched. Before that it still resolves from the system — so a first-time
  // visitor gets the theme their machine is set to rather than whichever we
  // would have hard-coded, and the switch simply starts where they already are.
  const [theme, setTheme] = useState(() => effectiveTheme());

  useEffect(() => {
    const onAuth = (e) => {
      setSignedIn(!!e.detail?.signedIn);
      setName(e.detail?.signedIn ? e.detail?.name || readName() : null);
    };
    window.addEventListener('decon:auth', onAuth);
    return () => window.removeEventListener('decon:auth', onAuth);
  }, []);

  const setMode = (wantLight) => {
    const next = wantLight ? 'light' : 'dark';
    applyTheme(next);
    setTheme(next);
  };

  const goHome = () => {
    window.location.href = '/';
  };

  const handleClick = (mode) => () => {
    onChangeRightMode(mode);
  };

  return (
    <header className="app-header">
      <div className="brand" onClick={goHome} style={{ cursor: 'pointer' }}>
        <div className="logo">VAUOM</div>
        <div>
          <div className="project-title"></div>
          <div className="small muted"></div>
        </div>
      </div>
      <div className="header-actions">
        <Switch
          on={theme === 'light'}
          onChange={setMode}
          label={theme === 'light' ? 'Light' : 'Dark'}
          title={`${theme === 'light' ? 'Light' : 'Dark'} theme. Switch to ${theme === 'light' ? 'dark' : 'light'}.`}
        />
        <button
          className={`btn ${rightMode === 'message' ? 'active' : ''}`}
          onClick={handleClick('message')}
        >
          Message
        </button>
        <button
          className={`btn ${rightMode === 'network' ? 'active' : ''}`}
          onClick={handleClick('network')}
        >
          Network
        </button>
        <button
          className={`btn account-btn ${signedIn ? 'btn--signed-in' : ''} ${rightMode === 'login' ? 'active' : ''}`}
          onClick={handleClick('login')}
          title={signedIn
            ? `Signed in as ${name || 'your account'} — open your account`
            : 'Sign in or create an account'}
        >
          {/* A name says who is signed in, which "Logged in" never did — and on a
              shared machine that is the difference between noticing you are in
              someone else's account and not. Only the first name: the button sits
              in a row of three and a full name would push the others off. It
              falls back to "Account" if the name has not arrived yet, never to a
              blank button. */}
          {signedIn ? (firstName(name) || 'Account') : 'Login'}
        </button>
      </div>
    </header>
  );
}

export default Header;
